import type { AIEvidenceCard } from '@cadencia/contracts';

/**
 * La procedencia de una ficha: que salga de algo que se recuperó de verdad.
 *
 * ESTE ES EL MECANISMO DEL §14, y conviene decir exactamente qué garantiza.
 *
 * «El modelo debe referirse a fuentes recuperadas y registradas, no inventar
 * enlaces dentro del texto libre.» La forma habitual de intentarlo es pedirle
 * al modelo que no invente. Eso no es una garantía, es un ruego.
 *
 * Aquí la garantía es estructural: la Edge Function sabe qué URL trajeron las
 * herramientas de servidor —esa lista la produce la API, no el modelo— y la
 * manda como dato al lado de las fichas. Una ficha cuya URL no esté en esa
 * lista se cae. El modelo no puede falsear la lista porque no la escribe.
 *
 * LO QUE ESTO **NO** GARANTIZA, y hay que decirlo para no vender de más:
 *
 * * Que la URL recuperada sea el artículo que la ficha describe. Si el modelo
 *   descarga tres papers y atribuye los hallazgos del primero al segundo, esto
 *   no lo detecta. Contra eso protege leer la ficha, no una función.
 * * Que el DOI sea el de ese documento. Un DOI es texto que el modelo escribe;
 *   se conserva porque es lo que permite a una persona ir a comprobarlo, que es
 *   justo el sentido de «identificador verificable».
 * * Que lo que dice `findings` esté en el artículo.
 *
 * Lo que sí garantiza es que **la fuente existe y se visitó**, que es el suelo
 * sin el cual lo demás no se puede ni discutir.
 */

export interface RetrievedVerdict {
  /** Fichas cuya procedencia está comprobada. */
  accepted: AIEvidenceCard[];
  /** Las que se caen, con el motivo. Se registra: dice mucho del prompt. */
  rejected: { title: string; reason: string }[];
}

/** Compara URL ignorando diferencias que no cambian el documento. */
function normalizar(url: string): string {
  return url.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();
}

/**
 * ¿Alguna URL recuperada corresponde a este DOI o PMID?
 *
 * Un DOI corresponde si aparece en la URL (doi.org/<doi> o la página del
 * editor que lo lleva en la ruta); un PMID, si la URL es su página de PubMed o
 * lo lleva como parámetro. Sin distinguir mayúsculas: los DOI no las
 * distinguen.
 */
/** El PMID de una página de PubMed o de un enlace con `pmid=`. */
const PMID_EN_URL = /(?:pubmed\.ncbi\.nlm\.nih\.gov\/|[?&]pmid=|\/pubmed\/)(\d+)/;

function identificadorRecuperado(
  doi: string | null,
  pmid: string | null,
  recuperadas: readonly string[],
): boolean {
  const doiNorm = doi?.trim().toLowerCase() ?? null;
  const pmidNorm = pmid?.trim() ?? null;
  return recuperadas.some((url) => {
    let u: string;
    try {
      u = decodeURIComponent(url).toLowerCase();
    } catch {
      // Una URL mal codificada se compara tal cual: no puede tumbar la revisión.
      u = url.toLowerCase();
    }
    if (doiNorm !== null && doiNorm !== '' && u.includes(doiNorm)) return true;
    if (pmidNorm !== null && pmidNorm !== '') {
      const deUrl = PMID_EN_URL.exec(u)?.[1];
      if (deUrl === pmidNorm) return true;
    }
    return false;
  });
}

/**
 * Cruza las fichas con lo que de verdad se recuperó.
 *
 * Una ficha CON url se acepta si esa URL se visitó. Una SIN url, solo si su
 * DOI o su PMID corresponde a alguna URL recuperada.
 *
 * HASTA EL 23/09/2026 UNA FICHA SIN URL PASABA CON SOLO TRAER UN DOI, con el
 * argumento de que «un DOI también se comprueba». Se comprueba si alguien lo
 * comprueba, y aquí nadie lo hacía: un DOI inventado con formato válido
 * quedaba como procedencia verificada. La presencia de un identificador no
 * prueba que la fuente exista (encargo, §3 y §10).
 *
 * Y aun aceptada, esto solo dice que la FUENTE EXISTE Y SE VISITÓ. Que su
 * contenido respalde la afirmación, y que sea aplicable a esta persona, son
 * otras dos preguntas: las contesta una persona al activar la ficha
 * (`canPromote` y la pantalla de Evidencia), no esta función.
 */
export function validateRetrieved(
  cards: readonly AIEvidenceCard[],
  retrievedUrls: readonly string[],
): RetrievedVerdict {
  const recuperadas = new Set(retrievedUrls.map(normalizar));

  const accepted: AIEvidenceCard[] = [];
  const rejected: { title: string; reason: string }[] = [];

  for (const card of cards) {
    if (card.url === null) {
      if (card.doi === null && card.pmid === null) {
        rejected.push({ title: card.title, reason: 'sin URL, DOI ni PMID: no se puede localizar' });
        continue;
      }
      if (!identificadorRecuperado(card.doi, card.pmid, retrievedUrls)) {
        rejected.push({
          title: card.title,
          reason: 'el DOI o PMID no corresponde a ninguna fuente recuperada en esta búsqueda',
        });
        continue;
      }
      accepted.push(card);
      continue;
    }

    if (!recuperadas.has(normalizar(card.url))) {
      rejected.push({
        title: card.title,
        reason: 'la URL no está entre las que se recuperaron en esta búsqueda',
      });
      continue;
    }

    accepted.push(card);
  }

  return { accepted, rejected };
}
